/**
 * FirstTradeGateModal
 *
 * Shown to a user immediately after their first-ever trade when their
 * profile_completion_stage is still 'signup_complete'.
 *
 * Collects:  surname + SA cellphone number
 * Triggers:  Supabase phone OTP
 * On verify: sets profile_completion_stage = 'phone_verified' in users table
 */
import React, { useState } from 'react';
import { Phone, X, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

interface FirstTradeGateModalProps {
  onClose: () => void;
}

type Step = 'collect' | 'verify' | 'done';

export function FirstTradeGateModal({ onClose }: FirstTradeGateModalProps) {
  const [step, setStep] = useState<Step>('collect');
  const [surname, setSurname] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatSAPhone = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('0') && digits.length === 10) return `+27${digits.slice(1)}`;
    if (digits.startsWith('27') && digits.length === 11) return `+${digits}`;
    return raw;
  };

  const handleSendOTP = async () => {
    setError(null);
    if (!surname.trim()) { setError('Please enter your surname'); return; }
    const formattedPhone = formatSAPhone(phone);
    if (!/^\+27\d{9}$/.test(formattedPhone)) {
      setError('Please enter a valid South African cellphone number (e.g. 0821234567)');
      return;
    }
    setLoading(true);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
      });
      if (otpError) throw otpError;
      setStep('verify');
    } catch (err: unknown) {
      logger.error('OTP send error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError(null);
    if (otp.length < 4) { setError('Please enter the full OTP code'); return; }
    setLoading(true);
    try {
      const formattedPhone = formatSAPhone(phone);
      const { error: verifyError } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: otp,
        type: 'sms',
      });
      if (verifyError) throw verifyError;

      // Update user profile in public.users
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('users')
          .update({
            user_metadata: {
              ...((user.user_metadata as Record<string, unknown>) ?? {}),
              surname: surname.trim(),
              phone: formatSAPhone(phone),
            },
            profile_completion_stage: 'phone_verified',
          })
          .eq('id', user.id);
      }
      setStep('done');
    } catch (err: unknown) {
      logger.error('OTP verify error:', err);
      setError(err instanceof Error ? err.message : 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
        {step !== 'done' && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {step === 'done' ? (
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">You're all set!</h2>
            <p className="text-gray-600 mb-6">Your phone number has been verified. Your profile is now more secure.</p>
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-wayja-teal text-white rounded-xl font-medium hover:bg-opacity-90 transition-colors"
            >
              Continue Trading
            </button>
          </div>
        ) : step === 'collect' ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-wayja-teal/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-wayja-teal" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Verify your number</h2>
                <p className="text-sm text-gray-500">One quick step to protect your account</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-5">
              Great job placing your first trade! To keep your account secure and
              qualify for payouts, please verify your cell number.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="e.g. Dlamini"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cell Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 0821234567"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              <button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full px-6 py-3 bg-wayja-teal text-white rounded-xl font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
              <button
                onClick={onClose}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
              >
                Skip for now
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-wayja-teal/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-wayja-teal" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Enter your OTP</h2>
                <p className="text-sm text-gray-500">We sent a code to {formatSAPhone(phone)}</p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OTP Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length < 4}
                className="w-full px-6 py-3 bg-wayja-teal text-white rounded-xl font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button
                onClick={() => { setStep('collect'); setOtp(''); setError(null); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
              >
                ← Change number
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
